import { configureStore } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'

import NotificationNoticesSlice from '~/core/slices/notification-notices'
import SystemSlice from '~/core/slices/system'
import UiSlice from '~/core/slices/ui'
import { FmApi } from '~/services/fm'
import { WebApi } from '~/services/web'
import { errorHandling } from '~/shared/middleware/error-handling'

/**
 * 使用 Redux Toolkit 构建的全局状态管理系统。
 *
 * 下面是一些确定应该将哪种数据放入 Redux 的常见经验法则：
 * 1. 应用程序的其他部分是否关心这些数据？
 * 2. 需要能够基于此原始数据创建进一步的衍生数据吗？
 * 3. 相同的数据是否用于驱动多个组件？
 * 4. 能够将此状态恢复到给定的时间点（即时间旅行）是有价值的吗？
 * 5. 是否需要缓存数据？
 * 对于不需要进行全局管理的状态，请将其放入 React 组件中。
 *
 * 对于 API 返回的数据，一般不需要使用范式化数据存储。
 * RTK Query 已经提供了很完善的乐观更新、悲观更新、缓存失效、手动本地更新的方法。
 * 如果有特殊需求，可以考虑将单个接口的响应数据标准化存储为查找表以方便使用。
 */
const store = configureStore({
  reducer: {
    [SystemSlice.name]: SystemSlice.reducer,
    [UiSlice.name]: UiSlice.reducer,
    [FmApi.reducerPath]: FmApi.reducer,
    [WebApi.reducerPath]: WebApi.reducer,
    [NotificationNoticesSlice.name]: NotificationNoticesSlice.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(FmApi.middleware, WebApi.middleware, errorHandling),
})

export default store

setupListeners(store.dispatch)

// Infer the `RootState` and `AppDispatch` types from the store itself
export type AppState = ReturnType<typeof store.getState>
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch
